import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ArrowRight,
  Target,
  Package,
  Shield,
  Clock,
  Users,
  CheckCircle,
  Fuel,
  Truck,
  Ship,
  Plane,
  Warehouse,
  FileCheck,
  BarChart3,
  Cpu,
  Eye,
  Heart,
  Award,
  Globe,
  TrendingUp,
  Globe2,
  MapPin,
  PlayCircle,
  ChevronLeft,
  ChevronRight,
  X,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

// ─── dados estáticos (sem texto traduzível) ──────────────────────────────────

const VALUE_ICONS = [
  <Target className="w-8 h-8" />,
  <Globe className="w-8 h-8" />,
  <Shield className="w-8 h-8" />,
  <TrendingUp className="w-8 h-8" />,
  <Heart className="w-8 h-8" />,
  <Award className="w-8 h-8" />,
  <Users className="w-8 h-8" />,
]

const SERVICES_STATIC = [
  { code: 'AIR', icon: <Plane className="w-4 h-4" /> },
  { code: 'MAR', icon: <Ship className="w-4 h-4" /> },
  { code: 'ROD', icon: <Truck className="w-4 h-4" /> },
  { code: 'PRJ', icon: <FileCheck className="w-4 h-4" /> },
  { code: 'ARM', icon: <Warehouse className="w-4 h-4" /> },
  { code: 'EMB', icon: <Package className="w-4 h-4" /> },
  { code: 'SCM', icon: <BarChart3 className="w-4 h-4" /> },
]

const BENEFIT_ICONS = [
  <Target className="w-8 h-8" />,
  <Shield className="w-8 h-8" />,
  <Clock className="w-8 h-8" />,
  <Users className="w-8 h-8" />,
]

const DIVISIONS_STATIC = [
  { code: 'ASD', image: 'ASD.jpg',   bgPosition: 'right center', bgSize: '45%', icon: <Plane className="w-4 h-4" /> },
  { code: 'NDA', image: 'NDA.jpg',   bgSize: '95%', bgPosition: 'right center', gradient: 'linear-gradient(to right, rgba(10,15,30,0.95) 0%, rgba(10,15,30,0.80) 50%, rgba(10,15,30,0.05) 100%)', icon: <Ship className="w-4 h-4" /> },
  { code: 'AHD', image: 'AHD.jpg',   bgPosition: 'right center', bgSize: '50%', gradient: 'linear-gradient(to right, rgba(10,15,30,1) 0%, rgba(10,15,30,0.97) 55%, rgba(10,15,30,0.0) 100%)', icon: <Shield className="w-4 h-4" /> },
  { code: 'SPD', image: 'SPD.jpg',   bgPosition: 'right center', bgSize: '60%', icon: <FileCheck className="w-4 h-4" /> },
  { code: 'ESD', image: 'ESD.jpg',   bgSize: '65%', bgPosition: 'right center', icon: <Package className="w-4 h-4" /> },
  { code: 'OGD', image: 'OGD.jpg',   bgSize: '65%', bgPosition: 'right center', icon: <Fuel className="w-4 h-4" /> },
  { code: 'ITD', image: 'ITD.jpeg',  bgSize: '65%', bgPosition: 'right center', icon: <Cpu className="w-4 h-4" /> },
]

const CERT_IMAGES = [
  'cert/WFG.png', 'cert/Iata.png', 'cert/Fiata.jfif', 'cert/ABAG.png',
  'cert/OEA.webp', 'cert/Anvisa.jpg', 'cert/RioGaleao.jpg', 'cert/RioGaleao.jpg',
  'cert/Trace.png', 'cert/TLF_OVERSEAS.jpg',
]

const CASE_YOUTUBE = ['ORVkTzJZZuE', 'RcbjjFU_bnw', 'm8CYCEjj0ww']

const galeria = [
  '1.jpg','2.jpg','3.png','4.jpeg','5.png','6.jpg','7.jpg','8.jpg','9.jpg','10.jpg',
  '11.jpg','12.jpg','13.jpg','14.jpg','15.jpeg','16.jpeg','17.jpg','18.jpg','19.jpg','20.jpg',
  '21.jpg','22.jpg','23.jpeg','24.jpeg','25.jpeg','26.jpeg','27.jpeg','28.jpeg','29.jpeg','30.jpeg',
]

// ─── componente principal ────────────────────────────────────────────────────

const CAPAS = ['CAPA1.png', 'CAPA2.png', 'Capa3.jpg']

const Home = () => {
  const { t } = useTranslation()
  const [capaIndex, setCapaIndex] = useState(0)
  const [activeDivision, setActiveDivision] = useState('ASD')
  const [activeService, setActiveService] = useState('AIR')
  const [current, setCurrent] = useState(0)
  const [lightbox, setLightbox] = useState(null)

  const heroRef = useRef(null)

  // ── arrays traduzíveis (recalculados quando muda o idioma) ──────────────────

  const values = VALUE_ICONS.map((icon, i) => ({
    icon,
    title: t(`quemSomos.valores.items.${i}.title`),
    description: t(`quemSomos.valores.items.${i}.description`),
  }))

  const presenceHighlights = t('presencaGlobal.highlights', { returnObjects: true })

  const SERVICES = SERVICES_STATIC.map((s) => {
    const processRaw = t(`servicos.items.${s.code}.process`, { returnObjects: true })
    return {
      ...s,
      title: t(`servicos.items.${s.code}.title`),
      description: t(`servicos.items.${s.code}.description`),
      highlight: t(`servicos.items.${s.code}.highlight`),
      points: t(`servicos.items.${s.code}.points`, { returnObjects: true }),
      process: Array.isArray(processRaw) ? processRaw : undefined,
    }
  })

  const benefits = BENEFIT_ICONS.map((icon, i) => ({
    icon,
    title: t(`diferenciais.items.${i}.title`),
    items: t(`diferenciais.items.${i}.items`, { returnObjects: true }),
  }))

  const specializedDivisions = DIVISIONS_STATIC.map((d) => ({
    ...d,
    name: t(`divisoes.items.${d.code}.name`),
    description: t(`divisoes.items.${d.code}.description`),
    highlight: t(`divisoes.items.${d.code}.highlight`),
    points: t(`divisoes.items.${d.code}.points`, { returnObjects: true }),
  }))

  const certifications = CERT_IMAGES.map((image, i) => ({
    image,
    text: t(`certificacoes.items.${i}`),
  }))

  const casesData = CASE_YOUTUBE.map((youtube, i) => ({
    youtube,
    title: t(`cases.items.${i}.title`),
    description: t(`cases.items.${i}.description`),
  }))

  // ── selecionados ────────────────────────────────────────────────────────────

  const selectedDivision = specializedDivisions.find((d) => d.code === activeDivision) || specializedDivisions[0]
  const selectedService = SERVICES.find((s) => s.code === activeService) || SERVICES[0]

  const prev = useCallback(() => setCurrent((i) => (i - 1 + galeria.length) % galeria.length), [])
  const next = useCallback(() => setCurrent((i) => (i + 1) % galeria.length), [])

  // Rotação das capas a cada 7 segundos
  useEffect(() => {
    const timer = setInterval(() => setCapaIndex((i) => (i + 1) % CAPAS.length), 7000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    const timer = setInterval(next, 4000)
    return () => clearInterval(timer)
  }, [next])

  useEffect(() => {
    const onKey = (e) => {
      if (lightbox !== null) {
        if (e.key === 'ArrowLeft') setLightbox((i) => (i - 1 + galeria.length) % galeria.length)
        if (e.key === 'ArrowRight') setLightbox((i) => (i + 1) % galeria.length)
        if (e.key === 'Escape') setLightbox(null)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [lightbox])

  return (
    <div className="overflow-x-hidden">

      {/* ── CAPA ─────────────────────────────────────────────────────────── */}
      <section
        id="capa"
        ref={heroRef}
        className="relative min-h-screen flex items-center overflow-hidden scroll-mt-0 text-white"
      >
        {/* Fundo com crossfade entre capas */}
        <AnimatePresence mode="sync">
          {capaIndex === 2 ? (
            /* CAPA3 — animação de navio navegando */
            <motion.div
              key={capaIndex}
              className="absolute inset-0 overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.5, ease: 'easeInOut' }}
            >
              <motion.div
                className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${import.meta.env.BASE_URL}images/Capa3.jpg)` }}
                animate={{ scale: [1.05, 1.14, 1.05], x: [0, -60, 0] }}
                transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
              />
            </motion.div>
          ) : (
            /* CAPA1 e CAPA2 — crossfade normal */
            <motion.div
              key={capaIndex}
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${import.meta.env.BASE_URL}images/${CAPAS[capaIndex]})` }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.5, ease: 'easeInOut' }}
            />
          )}
        </AnimatePresence>

        {/* Overlay escuro */}
        <div className="absolute inset-0 bg-gradient-to-r from-dark-900/80 via-dark-900/60 to-dark-900/30" />

        {/* Mapa no canto superior direito */}
        <motion.div
          className="absolute top-16 right-0 w-[55%] md:w-[48%] h-full pointer-events-none select-none"
          animate={{ x: [0, -160, -60, -200, 0], y: [0, -25, 20, -15, 0], scale: [1, 1.18, 0.97, 1.12, 1] }}
          transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
        >
          <img
            src={`${import.meta.env.BASE_URL}images/Mapa.png`}
            alt=""
            className="w-full h-full object-contain object-right-top opacity-50"
          />
        </motion.div>

        {/* Conteúdo — hero texto fixo em inglês (brand identity) */}
        <div className="relative z-10 pt-28 pb-20 flex items-center min-h-screen px-4 sm:px-6 lg:px-8">
          <motion.div
            className="flex flex-col items-start text-left max-w-2xl"
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
          >
            {/* We Are */}
            <motion.p
              className="text-lg md:text-xl lg:text-2xl font-heading font-semibold tracking-widest uppercase mb-1"
              style={{ color: '#38bdf8' }}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
            >
              We Are
            </motion.p>

            {/* BEYOND */}
            <motion.h1
              className="text-4xl md:text-5xl lg:text-6xl font-heading font-black leading-none tracking-tight text-white flex items-baseline gap-0"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
            >
              <span className="relative inline-block overflow-hidden">
                BEYOND<span style={{ color: '#38bdf8' }}>.</span>
                <motion.span
                  key={capaIndex}
                  className="absolute inset-0 pointer-events-none"
                  style={{ background: 'linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.22) 50%, transparent 70%)', width: '30%' }}
                  initial={{ x: '-100%', opacity: 1 }}
                  animate={{ x: '300%', opacity: [1, 1, 0] }}
                  transition={{ duration: 1.2, ease: 'easeInOut', delay: 0.4 }}
                />
              </span>
            </motion.h1>

            {/* Traço azul */}
            <motion.div
              className="h-1 rounded-full mt-3 mb-5"
              style={{ backgroundColor: '#38bdf8', width: '27%' }}
              initial={{ scaleX: 0, originX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.8, delay: 0.7, ease: 'easeOut' }}
            />

            {/* Taglines — fixas em inglês */}
            <motion.div
              className="space-y-1"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.9 }}
            >
              <p className="text-lg md:text-xl font-heading font-semibold tracking-wide text-white/90">
                Beyond borders. Beyond Limits.
              </p>
              <p className="text-base md:text-lg font-sans text-white/65 tracking-wide">
                Delivery mission-critical logistics worldwide.
              </p>
            </motion.div>
          </motion.div>
        </div>

        {/* Indicador de scroll */}
        <motion.div className="absolute bottom-10 left-1/2 -translate-x-1/2" animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex items-start justify-center p-2 backdrop-blur-sm">
            <motion.div className="w-1 h-3 bg-white/70 rounded-full" animate={{ y: [0, 10, 0] }} transition={{ repeat: Infinity, duration: 1.5 }} />
          </div>
        </motion.div>
      </section>


      {/* ── QUEM SOMOS + MISSÃO / VISÃO ───────────────────────────────────── */}
      <section
        id="quem-somos"
        className="scroll-mt-24 text-white section-padding"
        style={{ backgroundColor: '#0C2D54' }}
      >
        <div className="container-custom">

          <div className="flex flex-col lg:flex-row gap-6">

            {/* Quem Somos — meia tela */}
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="lg:w-1/2 rounded-2xl p-7 flex flex-col"
              style={{ backgroundColor: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}
            >
              <h2 className="text-xl font-heading font-bold mb-4">
                {t('quemSomos.title')} <span style={{ color: '#38bdf8' }}>{t('quemSomos.titleHighlight')}</span>
              </h2>
              <div className="space-y-3 text-base text-white/80 leading-relaxed">
                <p>{t('quemSomos.p1')}</p>
                <p>{t('quemSomos.p2')}</p>
                <p>{t('quemSomos.p3')}</p>
              </div>
            </motion.div>

            {/* Missão e Visão empilhados — meia tela */}
            <div className="lg:w-1/2 flex flex-col gap-6">

              {/* Nossa Missão */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
                className="flex-1 rounded-2xl p-7"
                style={{ backgroundColor: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}
              >
                <h3 className="text-xl font-heading font-bold mb-4">
                  {t('quemSomos.missao.title')} <span style={{ color: '#38bdf8' }}>{t('quemSomos.missao.titleHighlight')}</span>
                </h3>
                <p className="text-base text-white/80 leading-relaxed">{t('quemSomos.missao.text')}</p>
              </motion.div>

              {/* Nossa Visão */}
              <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="flex-1 rounded-2xl p-7"
                style={{ backgroundColor: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}
              >
                <h3 className="text-xl font-heading font-bold mb-4">
                  {t('quemSomos.visao.title')} <span style={{ color: '#38bdf8' }}>{t('quemSomos.visao.titleHighlight')}</span>
                </h3>
                <p className="text-base text-white/80 leading-relaxed">{t('quemSomos.visao.text')}</p>
              </motion.div>

            </div>

          </div>

          {/* Nossos Valores */}
          <div className="mt-14">
            <h3 className="text-xl md:text-2xl font-heading font-bold mb-2">
              {t('quemSomos.valores.title')} <span style={{ color: '#38bdf8' }}>{t('quemSomos.valores.titleHighlight')}</span>
            </h3>
            <p className="text-sm text-white/60 mb-8">{t('quemSomos.valores.subtitle')}</p>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {values.map((value, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.06 }}
                  viewport={{ once: true }}
                  className="rounded-xl p-4 flex flex-col items-start gap-3 hover:bg-white/10 transition-colors duration-200"
                  style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(56,189,248,0.15)' }}>
                    {React.cloneElement(value.icon, { className: 'w-4 h-4', style: { color: '#38bdf8' } })}
                  </div>
                  <div>
                    <h4 className="text-base font-bold text-white mb-1 leading-tight">{value.title}</h4>
                    <p className="text-sm text-white/60 leading-relaxed">{value.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

        </div>
      </section>

      {/* ── PRESENÇA GLOBAL ───────────────────────────────────────────────── */}
      <section id="presenca-global" className="flex flex-col lg:flex-row text-white scroll-mt-24">

        {/* Lado esquerdo — conteúdo */}
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="flex-1 section-padding flex items-center"
          style={{ backgroundColor: '#0C2D54' }}
        >
          <div className="max-w-xl mx-auto lg:mx-0 lg:pl-8 xl:pl-16">
            <p className="text-sm font-semibold tracking-[0.25em] uppercase mb-5" style={{ color: '#38bdf8' }}>
              {t('presencaGlobal.label')}
            </p>

            <h2 className="text-4xl md:text-5xl font-heading font-bold leading-tight mb-6">
              {t('presencaGlobal.title')}{' '}
              <span style={{ color: '#38bdf8' }}>{t('presencaGlobal.titleHighlight')}</span>
            </h2>

            <p className="text-base text-white/70 leading-relaxed mb-10">
              {t('presencaGlobal.text')}
            </p>

            <div className="grid sm:grid-cols-2 gap-4">
              {(Array.isArray(presenceHighlights) ? presenceHighlights : []).map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.07 }}
                  viewport={{ once: true }}
                  className="flex items-start gap-3 rounded-xl p-4"
                  style={{ backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
                >
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: 'rgba(56,189,248,0.15)' }}>
                    {index === 0 ? <MapPin size={16} style={{ color: '#38bdf8' }} /> : <CheckCircle size={16} style={{ color: '#38bdf8' }} />}
                  </div>
                  <p className="text-sm text-white/80 leading-relaxed">{item}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Lado direito — mapa */}
        <div className="hidden lg:flex w-[48%] flex-shrink-0 items-center justify-center" style={{ backgroundColor: '#0C2D54' }}>
          <img
            src={`${import.meta.env.BASE_URL}images/MAPAr.png`}
            alt="Mapa de presença global AGS"
            className="w-[500%] h-auto object-contain"
          />
        </div>

      </section>

      {/* ── SERVIÇOS ──────────────────────────────────────────────────────── */}
      <section id="servicos" className="scroll-mt-24 section-padding text-white" style={{ backgroundColor: '#0C2D54' }}>
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }} className="max-w-4xl mx-auto text-center mb-12">
            <p className="text-primary-400 text-sm tracking-[0.2em] font-semibold uppercase mb-4">{t('servicos.label')}</p>
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-white mb-6 leading-tight">
              {t('servicos.title')}{' '}
              <span className="text-primary-300">{t('servicos.titleHighlight')}</span>
            </h2>
            <p className="text-lg md:text-xl text-dark-200 leading-relaxed">{t('servicos.subtitle')}</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="text-center mb-10">
            <p className="text-primary-400 text-sm tracking-[0.2em] font-semibold uppercase mb-3">{t('servicos.selectLabel')}</p>
            <h3 className="heading-lg mb-3">{t('servicos.selectTitle')}</h3>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-5 md:gap-6 mb-8">
            {SERVICES.map((service) => {
              const isActive = service.code === activeService
              return (
                <button key={service.code} type="button" onClick={() => setActiveService(service.code)}
                  className={`w-[108px] h-[92px] md:w-[124px] md:h-[108px] rounded-xl border-2 transition-all duration-300 ${isActive ? 'border-primary-400 bg-primary-500/20 text-primary-300' : 'border-transparent bg-transparent text-dark-300 hover:text-primary-300'}`}>
                  <span className="flex h-full flex-col items-center justify-center gap-2">
                    {React.cloneElement(service.icon, { className: 'w-7 h-7 md:w-8 md:h-8' })}
                    <span className={`text-[11px] md:text-xs font-semibold tracking-wide ${isActive ? 'text-primary-300' : 'text-dark-400'}`}>{service.code}</span>
                  </span>
                </button>
              )
            })}
          </div>

          <motion.div key={selectedService.code} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="max-w-5xl mx-auto border border-white/15 rounded-2xl p-6 md:p-8" style={{ backgroundColor: 'rgba(255,255,255,0.07)' }}>
            <div className="flex items-start gap-4 mb-5">
              <div className="w-10 h-10 rounded-lg bg-primary-500/20 text-primary-300 flex items-center justify-center flex-shrink-0">{selectedService.icon}</div>
              <div className="min-w-0">
                <h4 className="text-2xl font-bold mb-2">{selectedService.title}</h4>
                <p className="text-dark-300">{selectedService.description}</p>
                <p className="inline-flex mt-3 px-3 py-1 rounded-full bg-primary-500/20 text-primary-300 text-xs font-semibold">{selectedService.highlight}</p>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              {(Array.isArray(selectedService.points) ? selectedService.points : []).map((point, i) => (
                <div key={i} className="flex items-center gap-2 border border-white/10 rounded-lg px-3 py-2 text-base text-dark-200" style={{ backgroundColor: 'rgba(255,255,255,0.07)' }}>
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0" />
                  <span>{point}</span>
                </div>
              ))}
            </div>
            {selectedService.process && (
              <div className="mt-8">
                <p className="text-primary-400 text-xs tracking-[0.2em] font-semibold uppercase mb-4">{t('servicos.processoTitle')}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {selectedService.process.map((item) => (
                    <div key={item.step} className="border border-white/10 rounded-xl p-4 flex flex-col items-center text-center gap-2" style={{ backgroundColor: 'rgba(255,255,255,0.07)' }}>
                      <span className="w-8 h-8 rounded-full bg-primary-500/20 border border-primary-400/40 flex items-center justify-center text-primary-300 text-sm font-bold flex-shrink-0">{item.step}</span>
                      <span className="text-white text-sm font-semibold leading-tight">{item.title}</span>
                      <span className="text-dark-400 text-xs leading-tight">{item.description}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* Diferenciais */}
      <section
        className="relative overflow-hidden text-white"
        style={{
          backgroundImage: `url(${import.meta.env.BASE_URL}images/QuemSomos2.png)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center right',
          minHeight: '580px',
        }}
      >
        <div
          className="absolute inset-0"
          style={{ background: 'linear-gradient(to right, rgba(12,45,84,1) 0%, rgba(12,45,84,0.95) 35%, rgba(12,45,84,0.6) 60%, rgba(12,45,84,0.0) 80%)' }}
        />

        <div className="relative z-10 section-padding">
          <div className="container-custom">
            <div className="max-w-[55%]">

              <div className="mb-10">
                <p className="text-primary-400 text-sm tracking-[0.2em] font-semibold uppercase mb-3">{t('diferenciais.label')}</p>
                <h3 className="text-4xl md:text-5xl font-heading font-bold mb-4">
                  {t('diferenciais.title')} <span style={{ color: '#38bdf8' }}>{t('diferenciais.titleHighlight')}</span>
                </h3>
                <p className="text-white/70 text-base max-w-xl">{t('diferenciais.subtitle')}</p>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                {benefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="rounded-xl p-5"
                    style={{ backgroundColor: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.13)', backdropFilter: 'blur(8px)' }}
                  >
                    <h4 className="text-lg font-bold mb-3 text-white">{benefit.title}</h4>
                    <ul className="text-white/75 text-base leading-relaxed space-y-1.5">
                      {(Array.isArray(benefit.items) ? benefit.items : []).map((item, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="text-primary-300 mt-1 leading-none">•</span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ))}
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* ── DIVISÕES ──────────────────────────────────────────────────────── */}
      <section id="divisoes" className="scroll-mt-24 section-padding text-white" style={{ backgroundColor: '#0C2D54' }}>
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="text-center mb-10">
            <p className="text-primary-400 text-sm tracking-[0.2em] font-semibold uppercase mb-3">{t('divisoes.label')}</p>
            <h2 className="heading-lg mb-3">{t('divisoes.title')}</h2>
            <p className="text-dark-300 max-w-2xl mx-auto">{t('divisoes.subtitle')}</p>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-5 md:gap-6 mb-8">
            {specializedDivisions.map((division) => {
              const isActive = division.code === selectedDivision.code
              return (
                <button key={division.code} type="button" onClick={() => setActiveDivision(division.code)}
                  className={`w-[108px] h-[92px] md:w-[124px] md:h-[108px] rounded-xl border-2 transition-all duration-300 ${isActive ? 'border-primary-400 bg-primary-500/20 text-primary-300' : 'border-transparent bg-transparent text-dark-300 hover:text-primary-300'}`}>
                  <span className="flex h-full flex-col items-center justify-center gap-2">
                    {React.cloneElement(division.icon, { className: 'w-7 h-7 md:w-8 md:h-8' })}
                    <span className={`text-[11px] md:text-xs font-semibold tracking-wide ${isActive ? 'text-primary-300' : 'text-dark-400'}`}>{division.code}</span>
                  </span>
                </button>
              )
            })}
          </div>

          <motion.div key={selectedDivision.code} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="max-w-5xl mx-auto rounded-2xl overflow-hidden"
            style={{ backgroundImage: `url(${import.meta.env.BASE_URL}images/${selectedDivision.image})`, backgroundSize: selectedDivision.bgSize || 'cover', backgroundPosition: selectedDivision.bgPosition || 'center', backgroundRepeat: selectedDivision.bgSize ? 'no-repeat' : 'repeat', boxShadow: 'inset 0 0 40px 20px rgba(0,0,0,0.85), 0 0 0 2px rgba(0,0,0,0.7)' }}>
            <div className="w-full h-full p-6 md:p-8" style={{ background: selectedDivision.gradient || 'linear-gradient(to right, rgba(10,15,30,1) 0%, rgba(10,15,30,0.97) 55%, rgba(10,15,30,0.05) 100%)' }}>
              <div className="flex items-start gap-4 mb-5">
                <div className="w-10 h-10 rounded-lg bg-primary-500/20 text-primary-300 flex items-center justify-center flex-shrink-0">{selectedDivision.icon}</div>
                <div className="min-w-0">
                  <h3 className="text-2xl font-bold mb-2">{selectedDivision.name}</h3>
                  <p className="text-dark-300">{selectedDivision.description}</p>
                  <p className="inline-flex mt-3 px-3 py-1 rounded-full bg-primary-500/20 text-primary-300 text-xs font-semibold">{selectedDivision.highlight}</p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                {(Array.isArray(selectedDivision.points) ? selectedDivision.points : []).map((point, i) => (
                  <div key={i} className="flex items-center gap-2 border border-white/10 rounded-lg px-3 py-2 text-sm text-dark-200" style={{ backgroundColor: 'rgba(255,255,255,0.07)' }}>
                    <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0" />
                    <span>{point}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── CERTIFICAÇÕES ─────────────────────────────────────────────────── */}
      <section id="certificacoes" className="scroll-mt-24 section-padding text-white" style={{ backgroundColor: '#0C2D54' }}>
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-3">
              {t('certificacoes.title')} <span style={{ color: '#38bdf8' }}>{t('certificacoes.titleHighlight')}</span>
            </h2>
            <p className="text-white/65 text-base">{t('certificacoes.subtitle')}</p>
          </div>
          <div className="grid md:grid-cols-2 gap-5 max-w-5xl mx-auto">
            {certifications.map((cert, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.07 }}
                viewport={{ once: true }}
                className="flex items-start gap-4 rounded-2xl p-5"
                style={{ backgroundColor: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}
              >
                <div className="w-16 h-16 flex-shrink-0 flex items-center justify-center rounded-xl bg-white p-2">
                  <img
                    src={`${import.meta.env.BASE_URL}images/${cert.image}`}
                    alt=""
                    className="w-full h-full object-contain"
                  />
                </div>
                <p className="text-white/85 text-sm leading-relaxed pt-1">{cert.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CASES ─────────────────────────────────────────────────────────── */}
      <section id="cases" className="scroll-mt-24 section-padding text-white" style={{ backgroundColor: '#0C2D54' }}>
        <div className="container-custom">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }} className="max-w-4xl mx-auto text-center mb-16">
            <div className="w-16 h-16 bg-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-6"><PlayCircle size={36} /></div>
            <h2 className="heading-lg mb-6">{t('cases.title')}</h2>
            <p className="text-xl md:text-2xl text-dark-200 leading-relaxed">{t('cases.subtitle')}</p>
          </motion.div>

          <div className="grid lg:grid-cols-3 gap-8 mb-16">
            {casesData.map((item, index) => (
              <motion.article key={item.youtube} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: index * 0.12 }} viewport={{ once: true }} className="rounded-3xl border border-white/10 shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300" style={{ backgroundColor: 'rgba(255,255,255,0.07)' }}>
                <div style={{ backgroundColor: 'rgba(0,0,0,0.2)' }}>
                  <iframe src={`https://www.youtube.com/embed/${item.youtube}`} title={item.title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen className="w-full aspect-video" />
                </div>
                <div className="p-8">
                  <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center text-white mb-5"><PlayCircle size={26} /></div>
                  <h3 className="text-2xl font-bold text-white mb-4">{item.title}</h3>
                  <p className="text-dark-300 leading-relaxed">{item.description}</p>
                </div>
              </motion.article>
            ))}
          </div>

          {/* Galeria */}
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="text-center mb-10">
            <p className="text-primary-400 text-sm tracking-[0.2em] font-semibold uppercase mb-3">{t('cases.galeriaLabel')}</p>
            <h3 className="heading-lg">{t('cases.galeriaTitle')}</h3>
          </motion.div>

          <div className="relative max-w-4xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden aspect-video bg-dark-800 shadow-2xl cursor-pointer" onClick={() => setLightbox(current)}>
              <AnimatePresence mode="wait">
                <motion.img key={current} src={`${import.meta.env.BASE_URL}images/Galeria/${galeria[current]}`} alt={`Foto ${current + 1}`} className="w-full h-full object-cover" initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.97 }} transition={{ duration: 0.5 }} />
              </AnimatePresence>
              <div className="absolute inset-0 bg-gradient-to-t from-dark-900/50 to-transparent pointer-events-none" />
              <span className="absolute bottom-4 right-4 text-white/60 text-sm font-medium">{current + 1} / {galeria.length}</span>
            </div>
            <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-dark-900/70 hover:bg-primary-600 text-white flex items-center justify-center transition-colors duration-200 backdrop-blur-sm"><ChevronLeft size={22} /></button>
            <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-dark-900/70 hover:bg-primary-600 text-white flex items-center justify-center transition-colors duration-200 backdrop-blur-sm"><ChevronRight size={22} /></button>
          </div>

          <div className="mt-6 flex gap-2 overflow-x-auto pb-2 max-w-4xl mx-auto">
            {galeria.map((foto, i) => (
              <button key={foto} onClick={() => setCurrent(i)} className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all duration-200 ${i === current ? 'border-primary-400 scale-105' : 'border-transparent opacity-60 hover:opacity-100'}`}>
                <img src={`${import.meta.env.BASE_URL}images/Galeria/${foto}`} alt={`Miniatura ${i + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </div>
      </section>


      {/* Lightbox */}
      <AnimatePresence>
        {lightbox !== null && (
          <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setLightbox(null)}>
            <button className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors" onClick={() => setLightbox(null)}><X size={20} /></button>
            <button className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-primary-600 text-white flex items-center justify-center transition-colors" onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + galeria.length) % galeria.length) }}><ChevronLeft size={26} /></button>
            <motion.img key={lightbox} src={`${import.meta.env.BASE_URL}images/Galeria/${galeria[lightbox]}`} alt={`Foto ${lightbox + 1}`} className="max-w-[90vw] max-h-[85vh] object-contain rounded-xl shadow-2xl" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} transition={{ duration: 0.3 }} onClick={(e) => e.stopPropagation()} />
            <button className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-primary-600 text-white flex items-center justify-center transition-colors" onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % galeria.length) }}><ChevronRight size={26} /></button>
            <span className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/50 text-sm">{lightbox + 1} / {galeria.length}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default Home
